import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'
import { renameVoiceAgent } from '@/reducers/agentProvisioning/agentProvisioning.actions'

import { NAME_MAX_LENGTH, nameLength, splitAgentName } from '../agentNameHelpers'

// Rename an agent (AVR-827). Open to anyone who can open the agent — customers
// included — because the endpoint behind it is gated on Studio permission
// rather than on the org-admin `edit_users` permission core's own user-PATCH
// requires.
//
// One field, not two. The name is stored as the agent user's `first_name` +
// `last_name`, but an agent is called "Lead Screening Agent" — it has no
// surname, so asking which half is which would be asking a question the product
// does not have an answer to. The server splits the line it is sent (first word,
// then the rest) and this form only mirrors that to bound the two halves before
// the round trip.
//
// Only the display name moves. `voice_agent_id` and `slug` identify the agent —
// the slug is a deployed code route and the URL prefix of every tool endpoint —
// so neither is editable here or reachable through the endpoint.
const RenameAgentModal = ({ show, onClose, voiceAgentId, currentName, orgId }) => {
  const dispatch = useDispatch()
  const saving = useSelector((state) => state.agentProvisioning.loading?.renameAgent)

  const [name, setName] = useState('')
  const [saveError, setSaveError] = useState(null)

  // Reset on open so reopening never shows the previous agent's name or a
  // stale error. `currentName` is the stored name only — never a config-title
  // fallback, or opening the form would stage a rename nobody asked for.
  useEffect(() => {
    if (show) {
      setName(currentName)
      setSaveError(null)
    }
  }, [show, currentName])

  const trimmed = name.trim().replace(/\s+/g, ' ')
  const { first, last } = splitAgentName(trimmed)

  // Mirror the BE's validation so the common mistakes are caught inline rather
  // than as a round trip. The server re-checks regardless — this is for the
  // message, not for safety. Length is per stored column, which is why a long
  // name can be fine while a single long word is not.
  const tooLongPart = [
    [first, 'first'],
    [last, 'last'],
  ].find(([part]) => nameLength(part) > NAME_MAX_LENGTH)

  const validationError = tooLongPart
    ? `The ${tooLongPart[1]} part of the name cannot exceed ${NAME_MAX_LENGTH} characters.`
    : null
  const emptyError = !isEmpty(name) && isEmpty(trimmed) ? 'Name is required.' : null
  const fieldError = emptyError ?? validationError

  const canSave = !isEmpty(trimmed) && !validationError && trimmed !== currentName

  const onSave = async () => {
    if (!canSave || saving) {
      return
    }
    setSaveError(null)
    const result = await dispatch(renameVoiceAgent({ voiceAgentId, name: trimmed, orgId }))
    if (result?.ok) {
      onClose()
      return
    }
    setSaveError(result?.message ?? 'Failed to rename agent')
  }

  // Enter saves, so the rename is a type-and-go edit rather than a reach for
  // the mouse — this is the smallest form on the page.
  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      onSave()
    }
  }

  return (
    <BasicModal
      title="Rename agent"
      show={show}
      onClose={onClose}
      size="tiny"
      footer={
        <>
          <Button onClick={onClose} data-testid="rename-agent-cancel">
            Cancel
          </Button>
          <Button
            brand
            disabled={!canSave || saving}
            loading={saving}
            onClick={onSave}
            data-testid="rename-agent-save"
          >
            Save
          </Button>
        </>
      }
    >
      <p className="voice-agents__rename-hint">
        Renaming changes what this agent is called everywhere it appears. Its ID and slug are
        unchanged, so calls, campaigns and history stay attached to it.
      </p>
      <FormField label="Name" required error={Boolean(fieldError)} disabled={saving}>
        <TextInput
          aria-label="Name"
          value={name}
          error={Boolean(fieldError)}
          autoFocus
          disabled={saving}
          onKeyDown={onKeyDown}
          onChange={(event) => setName(event.target.value)}
          data-testid="rename-agent-name"
        />
        {fieldError && <ErrorMessage content={fieldError} />}
      </FormField>
      {saveError && (
        <Banner critical header="Rename failed" data-testid="rename-agent-error">
          <p>{saveError}</p>
        </Banner>
      )}
    </BasicModal>
  )
}

export default RenameAgentModal
